import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Space, Tag, Modal, Form, Input, InputNumber, Select, DatePicker, Radio,
  message, Popconfirm, Descriptions, Card, Row, Col, Image, Tooltip, Empty, Alert, Checkbox,
} from 'antd';
import {
  ReloadOutlined, PlusOutlined, EditOutlined, DeleteOutlined, CalculatorOutlined,
  ScissorOutlined, FileAddOutlined, DollarOutlined, FolderOpenOutlined, EnvironmentOutlined, FileOutlined,
  CloudDownloadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useModuleApi } from '../ortak/moduleApi';
import { fmtAmount, fmtDate } from '../finans/shared/format';
import { inputNumberMoneyProps } from '../../utils/currency';
import DuzenleKisayolu from '../ortak/DuzenleKisayolu';
import { enhanceColumns, toplamGenislik } from '../ortak/tableHelpers';
import OdemeTalebiModal, {
  HesapAciklamasi, useCariSecenekleri, cariKartiAc, cakismaOnayliGonder, hataMesaji,
  benzerCariler, cariAcBaslik, tutarKontrolNedeni, referansDagit, TutarKontrolKutusu,
} from './OdemeTalebiModal';

const MINIO_BASE = 'https://dosya.atmturkiye.com/atmpoint/';
const sozlesmeFileUrl = (p) => `${MINIO_BASE}${String(p).replace(/^\/+/, '')}`;
const sozlesmeFilePaths = (r) => {
  if (!r || !r.gorsel) return [];
  try {
    const arr = JSON.parse(r.gorsel);
    return Array.isArray(arr) ? arr.filter(Boolean) : (typeof arr === 'string' ? [arr] : []);
  } catch {
    return typeof r.gorsel === 'string' && r.gorsel.includes('/') ? [r.gorsel] : [];
  }
};

const BANK_FATURA_TURU = [
  [1, 'Kira Geliri'], [2, 'Yeni Kira'], [3, 'Yenileme Kira'], [4, 'Sözleşme Yenileme'],
  [5, 'Kabin Geliri'], [6, 'Kabin Devir'], [7, 'Alt Kira'], [8, 'Elektrik'], [10, 'Temizlik'], [11, 'Ortak Gider'],
].map(([v, l]) => ({ value: v, label: l }));
const YS_FATURA_TURU = [[1, 'Sabit Kira'], [2, 'Banka Başı (girdikçe)'], [3, 'Boş Kabin']].map(([v, l]) => ({ value: v, label: l }));
const AYLAR = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: String(i + 1) }));

// =================================================================== Enflasyon
const ENF_FIELDS = [
  ['yil', 'Yıl', 'int'], ['ay', 'Ay', 'int'],
  ['bir_onceki_yilin_ayni_ayina_gore_ufe', 'Yıllık ÜFE', 'num'],
  ['bir_onceki_yilin_ayni_ayina_gore_tufe', 'Yıllık TÜFE', 'num'],
  ['12_aylik_ortalamaya_gore_ufe', '12A Ort. ÜFE', 'num'],
  ['12_aylik_ortalamaya_gore_tufe', '12A Ort. TÜFE', 'num'],
  ['ito_tefe', 'İTO/TEFE', 'num'],
];
// Formda olmayan, TCMB senkronunun yazdığı kolonlar
const ENF_ETIKET = {
  bir_onceki_yilin_aralik_ayina_gore_ufe: 'Aralığa göre ÜFE', bir_onceki_yilin_aralik_ayina_gore_tufe: 'Aralığa göre TÜFE',
};
export function EnflasyonTab({ api }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.list('enflasyon', { limit: 500 })); } catch { message.error('Yüklenemedi'); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); form.resetFields(); setOpen(true); };
  const openEdit = (r) => { setEditing(r); form.setFieldsValue(r); setOpen(true); };
  const submit = async () => {
    const v = await form.validateFields();
    try {
      if (editing) await api.update('enflasyon', editing.id, v);
      else await api.create('enflasyon', v);
      message.success('Kaydedildi'); setOpen(false); load();
    } catch (e) { message.error(e?.response?.data?.detail || 'Hata'); }
  };
  const del = async (r) => { try { await api.remove('enflasyon', r.id); message.success('Silindi'); load(); } catch { message.error('Silinemedi'); } };

  // 27.10.2026: TCMB EVDS senkronu — önce önizleme (son 12 ay farkları), onayla uygula
  const [tcmb, setTcmb] = useState(false);
  const tcmbGuncelle = async () => {
    setTcmb(true);
    try {
      const d = (await api.post('/inflation/tcmb-sync', { uygula: false })).data;
      if (!d.degisiklikler.length) {
        message.success(`Tablo TCMB ile aynı (son ${d.kontrol_edilen} ay, en son ${d.son_ay})`);
        return;
      }
      const satirlar = d.degisiklikler.flatMap((g) => Object.entries(g.kolonlar).map(([k, [eski, yeni]]) => ({
        key: `${g.yil}-${g.ay}-${k}`, donem: `${g.yil}-${String(g.ay).padStart(2, '0')}`, islem: g.islem,
        kolon: (ENF_FIELDS.find(([f]) => f === k) || [k, ENF_ETIKET[k] || k])[1], eski, yeni,
      })));
      Modal.confirm({
        title: `TCMB'ye göre ${d.degisiklikler.length} ay güncellenecek`, width: 680,
        content: (
          <Table size="small" pagination={false} dataSource={satirlar} scroll={{ y: 320 }} columns={[
            { title: 'Dönem', dataIndex: 'donem', width: 80 },
            { title: 'İşlem', dataIndex: 'islem', width: 90 },
            { title: 'Kolon', dataIndex: 'kolon' },
            { title: 'Tablodaki', dataIndex: 'eski', width: 90, render: (v) => (v == null ? '-' : `%${v}`) },
            { title: 'TCMB', dataIndex: 'yeni', width: 90, render: (v) => <b>%{v}</b> },
          ]} />
        ),
        okText: 'Uygula', cancelText: 'Vazgeç',
        onOk: async () => {
          try {
            const r = await api.post('/inflation/tcmb-sync', { uygula: true });
            message.success(r.message || 'Güncellendi'); load();
          } catch (e) { message.error(e?.response?.data?.detail || 'Güncellenemedi'); }
        },
      });
    } catch (e) {
      message.error(e?.response?.data?.detail || 'TCMB verisi alınamadı');
    } finally {
      setTcmb(false);
    }
  };

  const columns = [
    ...ENF_FIELDS.map(([k, t, type]) => ({ title: t, dataIndex: k, render: type === 'num' ? (v) => (v == null ? '' : `%${v}`) : undefined })),
    {
      title: 'İşlem', key: 'a', width: 100, render: (_, r) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="Silinsin mi?" onConfirm={() => del(r)} okText="Sil" cancelText="Vazgeç"><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ReloadOutlined />} onClick={load}>Yenile</Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>Yeni</Button>
        <Button icon={<CloudDownloadOutlined />} loading={tcmb} onClick={tcmbGuncelle}>TCMB'den Güncelle</Button>
        <span style={{ color: '#888' }}>{data.length} kayıt · her gün 09:15'te TCMB'den otomatik (prod)</span>
      </Space>
      <Table rowKey="id" size="small" loading={loading} dataSource={data} columns={enhanceColumns(columns)} pagination={{ pageSize: 50 }} scroll={{ x: toplamGenislik(enhanceColumns(columns)), y: 480 }} />
      <Modal title={editing ? 'Enflasyon Düzenle' : 'Yeni Enflasyon'} open={open} onOk={submit} onCancel={() => setOpen(false)} okText="Kaydet" cancelText="Vazgeç" destroyOnClose>
        <Form form={form} layout="vertical">
          {ENF_FIELDS.map(([k, t, type]) => (
            <Form.Item key={k} name={k} label={t} rules={k === 'yil' || k === 'ay' ? [{ required: true }] : []}>
              <InputNumber style={{ width: '100%' }} step={type === 'num' ? 0.01 : 1} />
            </Form.Item>
          ))}
        </Form>
      </Modal>
    </div>
  );
}

// =================================================================== Banka Fatura
// 26.10 düzeltmesi (F1/F2/F3/F5/F6 banka):
// - Aylık liste yıl+ay ile; etkin ödeme ayı (odeme_ay 0 ise türetilmiş), süresi geçmemiş sözleşmeler
//   ('bitmişleri de göster' ile hepsi) ve her satırda seçili ayın dönemine ait fatura durumu.
// - Dönem/Durum kolonları yüklemede eklenen görüntü alanlarına (donem_txt, durum_txt) bağlı: arama
//   ekranda görünen metinde, sıralama ISO tarihte. Görünüm değişince Table yeniden kurulur (filtre sızmaz).
// - Talepler sunucuda tarih aralığı + arama ile süzülür, 2000'lik dilimlerle gezilir (sessiz kesme yok).
// - Talep oluştururken mükerrer 409: çakışanlar gösterilir, yalnız açık onayla force ile açılır.
// - Bütün modallarda confirmLoading (çift tıklamayla iki talep açılmasın).
// 27.10 düzeltmesi (inceleme): talep 409'ları kodlu (mukerrer / imza / hesap); her onay 'onaylar'a eklenip
//   yeniden gönderilir. Endeks yayımlanmamışsa ya da baz zayıfsa modalda kırmızı uyarı, buton tehlike rengi.
//   bsk'siz lokasyon faturası yalnız tek bsk'li lokasyon+bankada sayılır; değilse 'Yok' + ipucu.
//   Sayfa boyutu seçici çalışsın diye pageSize değil defaultPageSize.
// 27.10 düzeltmesi: aynı fiziksel kabinin başka bsk'sine (yenilenen sözleşmenin eski bsk'si) bağlı dönem faturası
//   listede '... (önceki söz.)' (donem_eslesme 'kabin'), çakışma listelerinde 'önceki söz. bsk #..' olarak görünür.
const BANKA_DURUM_RENK = { 'Tahsil Edildi': 'green', Kesildi: 'blue', Talep: 'gold', 'İade': 'red', Yok: 'default' };
// to-cut donem_eslesme -> durum eki (arama metni / etiket); 'bsk' kendi faturası, ek yok
const BANKA_ESLESME_EK = { lokasyon: ' (lokasyon)', kabin: ' (önceki söz.)' };
const BANKA_ESLESME_KISA = { lokasyon: ' (lok.)', kabin: ' (önceki söz.)' };
const BANKA_BAZ_KAYNAGI = {
  onceki_fatura: 'Önceki fatura',
  onceki_fatura_lokasyon: "Önceki fatura (lokasyon, bsk'siz)",
  kiraBedeli: 'Sözleşme kira bedeli',
  yok: 'Bulunamadı',
};
const bankaBazLabel = (k) => BANKA_BAZ_KAYNAGI[k] || (/_kira$/.test(String(k || '')) ? `bsk ${String(k).replace('_kira', '')} kira kaydı` : (k || ''));
const bankaDonemTxt = (r) => `${fmtDate(r.donemBaslangic)} - ${fmtDate(r.donemBitis)}`;
const bankaFaturaTuruLabel = (v) => (BANK_FATURA_TURU.find((o) => o.value === Number(v)) || {}).label || (v ?? '');
const bankaCakismaListesi = (list) => (
  <ul style={{ paddingLeft: 18, margin: 0 }}>
    {(list || []).map((c) => (
      <li key={c.id}>
        {c.evrakNo || '(evraksız)'} · {fmtDate(c.donemBaslangic)} - {fmtDate(c.donemBitis)} · {fmtAmount(c.fatura_tutar)} · {c.durum}
        {c.fatura_turu_ad ? ` · ${c.fatura_turu_ad}` : ''}{c.eslesme === 'kabin' && c.bsk ? ` · önceki söz. bsk #${c.bsk}` : ''} (#{c.id})
      </li>
    ))}
  </ul>
);
const BANKA_ONAY_ACIKLAMA = {
  mukerrer: "Aynı bsk'ye bağlı ikinci bir gerçek kabin faturası gibi bilinçli bir durum değilse vazgeçin.",
  imza: 'Kapanış / kalan dönem faturası gibi bilinçli bir durum değilse vazgeçin.',
  hesap: 'Endeks yayımlanınca yeniden hesaplayın; tutarı elle kontrol edip düzelttiyseniz onaylayın.',
};
// 409 detail -> onay içeriği (kod: mukerrer | imza | hesap)
const bankaOnayIcerik = (det) => (
  <div>
    {det.kod === 'mukerrer' && (
      <>
        <p>Örtüşen geçerli kayıtlar:</p>
        {bankaCakismaListesi(det.cakisanlar)}
      </>
    )}
    {det.kod === 'hesap' && (
      <>
        <ul style={{ paddingLeft: 18, margin: 0 }}>{(det.sorunlar || []).map((x) => <li key={x}>{x}</li>)}</ul>
        <p style={{ marginTop: 8 }}>Önerilen {fmtAmount(det.onerilen_tutar)} · gönderilen {fmtAmount(det.gonderilen_tutar)}</p>
      </>
    )}
    <p style={{ marginTop: 8 }}>{BANKA_ONAY_ACIKLAMA[det.kod] || ''}</p>
  </div>
);
const bankaOnayKodu = (e) => {
  const det = e?.response?.data?.detail;
  if (e?.response?.status !== 409 || !det || typeof det !== 'object') return null;
  return det.kod || (det.cakisanlar ? 'mukerrer' : null);
};
const bankaHesapSorunlari = (d) => {
  const out = [];
  const eksik = (d && d.endeks_eksik) || [];
  if (eksik.length) out.push(`Endeks yayımlanmamış: ${eksik.join(', ')} — bu yılların artışı uygulanmadı (%0).`);
  if (d && d.baz_zayif) out.push('Fatura geçmişi yok; baz sözleşme kira bedeli üzerinden çok yıllık bileşik artış. Tutarı elle kontrol edin.');
  return out;
};
const BANKA_HESAP_UYARI_RE = /örtüşen|Endeks yayımlanmamış|Baz sözleşme kira bedeli x/;
const BANKA_YILLAR = (() => {
  const y = dayjs().year();
  return [y - 2, y - 1, y, y + 1].map((v) => ({ value: v, label: String(v) }));
})();
const BANKA_TALEP_DILIM = 2000;

export function BankaFaturaTab({ api }) {
  const listApi = useModuleApi('/list');
  const [view, setView] = useState('liste');
  const [yil, setYil] = useState(dayjs().year());
  const [ay, setAy] = useState(dayjs().month() + 1);
  const [bitmis, setBitmis] = useState(false);
  const [bskList, setBskList] = useState([]);
  const [talepler, setTalepler] = useState([]);
  const [talepToplam, setTalepToplam] = useState(0);
  const [talepOffset, setTalepOffset] = useState(0);
  const [talepStatus, setTalepStatus] = useState('to-cut');
  const [talepTarih, setTalepTarih] = useState(null);   // dönem başlangıcı aralığı [dayjs, dayjs]
  const [talepArama, setTalepArama] = useState('');
  const [mukerrer, setMukerrer] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [calc, setCalc] = useState(null);       // {row, data}
  const [cutRow, setCutRow] = useState(null);
  const [cForm] = Form.useForm();
  const [kForm] = Form.useForm();

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.get('/bank-invoice/to-cut', { ay, yil, bitmis: bitmis || undefined });
      setBskList((d || []).map((r) => ({
        ...r,
        ay_txt: `${r.etkin_ay || 'Tanımsız'}${r.ay_turetilmis ? ' (türetilmiş)' : ''}`,
        donem_durum_txt: r.donem_durum === 'Yok'
          ? `Yok${r.donem_lok_belirsiz ? ` (lokasyonda ${r.donem_lok_belirsiz} bsk'siz fatura)` : ''}`
          : `${r.donem_durum}${BANKA_ESLESME_EK[r.donem_eslesme] || ''}`,
      })));
    } catch { message.error('Yüklenemedi'); }
    finally { setLoading(false); }
  }, [api, ay, yil, bitmis]);
  const loadTalep = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.get('/bank-invoice/requests', {
        status: talepStatus, limit: BANKA_TALEP_DILIM, offset: talepOffset,
        tarih_start: talepTarih && talepTarih[0] ? talepTarih[0].format('YYYY-MM-DD') : undefined,
        tarih_end: talepTarih && talepTarih[1] ? talepTarih[1].format('YYYY-MM-DD') : undefined,
        genel_arama: talepArama || undefined,
      });
      setTalepler(((d && d.rows) || []).map((r) => ({ ...r, donem_txt: bankaDonemTxt(r), durum_txt: r.durum })));
      setTalepToplam((d && d.total) || 0);
    } catch { message.error('Yüklenemedi'); }
    finally { setLoading(false); }
  }, [api, talepStatus, talepOffset, talepTarih, talepArama]);
  const loadMukerrer = useCallback(async () => {
    setLoading(true);
    try {
      const d = await listApi.post('/execute-query/kontrol-bft-mukerrer', { params: {} });
      setMukerrer(Array.isArray(d) ? d : []);
    } catch { message.error('Yüklenemedi'); }
    finally { setLoading(false); }
  }, [listApi]);
  const reload = useCallback(() => {
    if (view === 'liste') return loadList();
    if (view === 'talepler') return loadTalep();
    return loadMukerrer();
  }, [view, loadList, loadTalep, loadMukerrer]);
  useEffect(() => { reload(); }, [reload]);

  const openCalc = async (row) => {
    try {
      const d = await api.get(`/bank-invoice/calc/${row.bsk_id}`, { yil, ay });
      setCalc({ row, data: d });
      cForm.setFieldsValue({
        donem_baslangic: d.donem_baslangic ? dayjs(d.donem_baslangic) : null,
        donem_bitis: d.donem_bitis ? dayjs(d.donem_bitis) : null,
        fatura_tutar: d.fatura_tutar, fatura_turu: d.onerilen_fatura_turu || 3, banka_kodu: '',
      });
    } catch (e) { message.error(e?.response?.data?.detail || 'Hesaplanamadı'); }
  };
  const createReq = async (onaylar = []) => {
    const v = await cForm.validateFields();
    const { row, data } = calc;
    setSaving(true);
    try {
      // lokasyon/kabin/banka backend'de bsk'den çözülür (banka = sözleşme bankası; bsk.banka çoğunlukla 0)
      await api.post('/bank-invoice/request', {
        bsk_id: row.bsk_id, firma: data.firma,
        donem_baslangic: dayjs(v.donem_baslangic).format('YYYY-MM-DD'),
        donem_bitis: dayjs(v.donem_bitis).format('YYYY-MM-DD'),
        fatura_tutar: v.fatura_tutar, fatura_turu: v.fatura_turu, banka_kodu: v.banka_kodu,
        onaylar: onaylar.length ? onaylar : undefined,
      });
      message.success('Talep oluşturuldu'); setCalc(null); loadList();
    } catch (e) {
      const det = e?.response?.data?.detail;
      const kod = bankaOnayKodu(e);
      if (kod && !onaylar.includes(kod)) {
        Modal.confirm({
          title: det.message || 'Onay gerekli',
          width: 640,
          content: bankaOnayIcerik({ ...det, kod }),
          okText: 'Yine de oluştur', okButtonProps: { danger: true }, cancelText: 'Vazgeç',
          onOk: () => createReq([...onaylar, kod]),
        });
      } else {
        message.error(typeof det === 'string' ? det : (det?.message || 'Hata'));
      }
    } finally { setSaving(false); }
  };
  const openCut = (r) => { setCutRow(r); kForm.setFieldsValue({ evrakNo: '', fatura_tarih: dayjs(), hesap: null }); };
  const doCut = async () => {
    const v = await kForm.validateFields();
    setSaving(true);
    try {
      await api.post(`/bank-invoice/${cutRow.id}/cut`, { evrakNo: v.evrakNo, fatura_tarih: dayjs(v.fatura_tarih).format('YYYY-MM-DD'), hesap: v.hesap });
      message.success('Fatura kesildi'); setCutRow(null); loadTalep();
    } catch (e) { message.error(e?.response?.data?.detail || 'Kesim hatası'); }
    finally { setSaving(false); }
  };

  const bskCols = [
    { title: 'Lokasyon', dataIndex: 'lokasyon_ad', ellipsis: true, render: (v, r) => v || r.lokasyon },
    { title: 'Banka', dataIndex: 'banka_ad', ellipsis: true },
    { title: 'Kabin', dataIndex: 'kabin', width: 70 },
    { title: 'Kira Bedeli', dataIndex: 'kiraBedeli', width: 130, render: (v) => fmtAmount(v) },
    { title: 'Söz. No', dataIndex: 'sozlesmeNo', width: 90 },
    {
      title: 'Söz. Bitiş', dataIndex: 'sozlesmeBitis', width: 150,
      render: (v, r) => (
        <span>{fmtDate(v)}{r.sure_doldu && <Tooltip title="Bitiş geçmiş; sözleşme otomatik uzamalı"><Tag color="orange" style={{ marginLeft: 4 }}>uzadı</Tag></Tooltip>}</span>
      ),
    },
    {
      title: 'Ödeme Ayı', dataIndex: 'ay_txt', width: 130,
      sorter: (a, b) => (Number(a.etkin_ay) || 0) - (Number(b.etkin_ay) || 0),
      render: (_, r) => (
        <span>{r.etkin_ay || '-'}{r.ay_turetilmis && <Tooltip title="bsk.odeme_ay boş; kira/sözleşme başlangıç ayından türetildi"><Tag color="orange" style={{ marginLeft: 4 }}>türetilmiş</Tag></Tooltip>}</span>
      ),
    },
    {
      title: 'Bu Dönem', dataIndex: 'donem_durum_txt', width: 210,
      render: (_, r) => (r.donem_durum === 'Yok'
        ? (
          <span>
            <Tag>Yok</Tag>
            {r.donem_lok_belirsiz > 0 && (
              <Tooltip title={`Bu lokasyon+bankada ${r.donem_lok_belirsiz} bsk'siz fatura var ama birden çok bsk olduğu için hangisine ait olduğu belli değil; talep açarken mükerrer kontrolü gösterir.`}>
                <Tag color="orange">lok.: {r.donem_lok_belirsiz} bsk'siz</Tag>
              </Tooltip>
            )}
          </span>
        )
        : (
          <Tooltip title={`${r.donem_adet} kayıt; ${r.donem_evrak || '(evraksız)'} · ${fmtDate(r.donem_baslangic)} · ${fmtAmount(r.donem_tutar)}${r.donem_eslesme === 'lokasyon' ? " · bsk'siz, lokasyon+banka eşleşmesi" : ''}${r.donem_eslesme === 'kabin' ? ` · aynı kabinin önceki sözleşme kaydına (bsk #${r.donem_bsk}) bağlı` : ''}`}>
            <Tag color={BANKA_DURUM_RENK[r.donem_durum]}>{r.donem_durum}{BANKA_ESLESME_KISA[r.donem_eslesme] || ''}</Tag>
            <span>{r.donem_evrak || ''}</span>
          </Tooltip>
        )),
    },
    {
      title: 'İşlem', key: 'a', fixed: 'right', width: 110,
      render: (_, r) => (
        <Button size="small" type={r.donem_durum === 'Yok' ? 'primary' : 'default'} icon={<CalculatorOutlined />} onClick={() => openCalc(r)}>Hesapla</Button>
      ),
    },
  ];
  const talepCols = [
    { title: 'Evrak No', dataIndex: 'evrakNo', width: 110 },
    { title: 'Lokasyon', dataIndex: 'lokasyon_ad', ellipsis: true, render: (v, r) => v || r.lokasyon },
    { title: 'Banka', dataIndex: 'banka_ad', ellipsis: true },
    { title: 'Kabin', dataIndex: 'kabin', width: 70 },
    { title: 'Tür', dataIndex: 'fatura_turu_ad', width: 120 },
    {
      title: 'Dönem', dataIndex: 'donem_txt', width: 220,
      sorter: (a, b) => String(a.donemBaslangic || '').localeCompare(String(b.donemBaslangic || '')),
    },
    { title: 'Tutar', dataIndex: 'fatura_tutar', width: 130, render: (v) => fmtAmount(v) },
    { title: 'Durum', dataIndex: 'durum_txt', width: 120, render: (v) => <Tag color={BANKA_DURUM_RENK[v]}>{v}</Tag> },
    { title: 'İşlem', key: 'a', fixed: 'right', width: 90, render: (_, r) => !r.kesildi && !r.iade && <Button size="small" type="primary" icon={<ScissorOutlined />} onClick={() => openCut(r)}>Kes</Button> },
  ];
  const mukerrerCols = [
    { title: 'Yıl', dataIndex: 'Yıl', width: 70 },
    { title: 'Lokasyon', dataIndex: 'Lokasyon', ellipsis: true },
    { title: 'Banka', dataIndex: 'Banka', width: 130 },
    { title: 'bsk', dataIndex: 'bsk', width: 80 },
    { title: 'Dönem Baş.', dataIndex: 'Dön. Baş. Tar.', width: 110, render: fmtDate },
    { title: 'Tür', dataIndex: 'Fatura Türü', width: 120, render: bankaFaturaTuruLabel },
    { title: 'Adet', dataIndex: 'Adet', width: 70 },
    { title: 'Evrak Noları', dataIndex: 'Evrak Noları', width: 200 },
    { title: 'Kabinler', dataIndex: 'Kabinler', width: 90 },
    { title: 'Tutarlar', dataIndex: 'Tutarlar', width: 200 },
    { title: 'Fazla Tutar', dataIndex: 'Fazla Tutar', width: 130, render: (v) => fmtAmount(v) },
    { title: 'Kesilen', dataIndex: 'Kesilen', width: 80 },
    { title: 'Tahsil', dataIndex: 'Tahsil Edilen', width: 80 },
    { title: 'Tip', dataIndex: 'Tip', width: 170 },
  ];
  const aktifCols = enhanceColumns(view === 'liste' ? bskCols : (view === 'talepler' ? talepCols : mukerrerCols));
  const aktifData = view === 'liste' ? bskList : (view === 'talepler' ? talepler : mukerrer);
  const aktifRowKey = view === 'liste' ? 'bsk_id' : (view === 'talepler' ? 'id' : 'key_bft_ids');
  const d = calc && calc.data;

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Radio.Group value={view} onChange={(e) => setView(e.target.value)} optionType="button" buttonStyle="solid"
          options={[{ value: 'liste', label: 'Aylık Liste (bsk)' }, { value: 'talepler', label: 'Talepler' }, { value: 'mukerrer', label: 'Mükerrer Kontrol' }]} />
        {view === 'liste' && (
          <>
            <Select value={yil} onChange={setYil} options={BANKA_YILLAR} style={{ width: 90 }} />
            <Select value={ay} onChange={setAy} options={AYLAR} style={{ width: 80 }} />
            <Checkbox checked={bitmis} onChange={(e) => setBitmis(e.target.checked)}>Süresi bitmişleri de göster</Checkbox>
          </>
        )}
        {view === 'talepler' && (
          <>
            <Select value={talepStatus} onChange={(v) => { setTalepStatus(v); setTalepOffset(0); }} style={{ width: 160 }}
              options={[{ value: 'to-cut', label: 'Kesilecek' }, { value: 'cut', label: 'Kesilmiş' }, { value: 'to-collect', label: 'Tahsil Edilecek' }, { value: 'collected', label: 'Tahsil Edilmiş' }]} />
            <DatePicker.RangePicker value={talepTarih} format="DD.MM.YYYY" placeholder={['Dönem baş. (ilk)', 'Dönem baş. (son)']}
              onChange={(v) => { setTalepTarih(v); setTalepOffset(0); }} />
            <Input.Search allowClear placeholder="Evrak / lokasyon / banka" style={{ width: 230 }}
              onSearch={(v) => { setTalepArama((v || '').trim()); setTalepOffset(0); }} />
          </>
        )}
        <Button icon={<ReloadOutlined />} onClick={reload}>Yenile</Button>
        {view === 'liste' && <span style={{ color: '#888' }}>{bskList.length} bsk · faturası olmayan {bskList.filter((r) => r.donem_durum === 'Yok').length}</span>}
        {view === 'mukerrer' && <span style={{ color: '#888' }}>{mukerrer.length} grup · salt-okur rapor (veri değişmez)</span>}
        {view === 'talepler' && (
          <Space size={4}>
            <span style={{ color: '#888' }}>
              Toplam {talepToplam} · {talepToplam ? `${talepOffset + 1}-${Math.min(talepOffset + BANKA_TALEP_DILIM, talepToplam)} yüklendi` : 'kayıt yok'}
            </span>
            {talepToplam > BANKA_TALEP_DILIM && (
              <>
                <Button size="small" disabled={talepOffset === 0} onClick={() => setTalepOffset(Math.max(0, talepOffset - BANKA_TALEP_DILIM))}>‹ Önceki</Button>
                <Button size="small" disabled={talepOffset + BANKA_TALEP_DILIM >= talepToplam} onClick={() => setTalepOffset(talepOffset + BANKA_TALEP_DILIM)}>Sonraki ›</Button>
              </>
            )}
          </Space>
        )}
      </Space>
      {view === 'talepler' && talepToplam > BANKA_TALEP_DILIM && (
        <Alert type="info" showIcon style={{ marginBottom: 8 }}
          message={`Kolon filtreleri yalnız yüklenen ${BANKA_TALEP_DILIM}'lik dilimde çalışır; tüm kayıtlarda aramak için tarih aralığı / arama kutusunu kullanın.`} />
      )}
      <Table key={view} rowKey={aktifRowKey} size="small" loading={loading}
        dataSource={aktifData} columns={aktifCols}
        scroll={{ x: toplamGenislik(aktifCols), y: 460 }} pagination={{ defaultPageSize: 50, showSizeChanger: true }} />

      <Modal title={`Fatura Hesapla & Talep Oluştur${calc ? ` — ${calc.row.lokasyon_ad || calc.row.lokasyon} / ${calc.row.banka_ad || ''}` : ''}`}
        open={!!calc} onOk={() => createReq([])} confirmLoading={saving} onCancel={() => setCalc(null)}
        okButtonProps={{ danger: !!d && ((d.mevcut_faturalar || []).length > 0 || bankaHesapSorunlari(d).length > 0) }}
        okText="Talep Oluştur" cancelText="Vazgeç" width={680} destroyOnClose>
        {d && (
          <>
            {(d.mevcut_faturalar || []).length > 0 && (
              <Alert type="error" showIcon style={{ marginBottom: 8 }} message="Bu dönemle örtüşen fatura/talep var"
                description={bankaCakismaListesi(d.mevcut_faturalar)} />
            )}
            {bankaHesapSorunlari(d).length > 0 && (
              <Alert type="error" showIcon style={{ marginBottom: 8 }} message="Hesap eksik veriyle yapıldı; talep açık onay ister"
                description={<ul style={{ paddingLeft: 18, margin: 0 }}>{bankaHesapSorunlari(d).map((u) => <li key={u}>{u}</li>)}</ul>} />
            )}
            {(d.uyarilar || []).filter((u) => !BANKA_HESAP_UYARI_RE.test(u)).length > 0 && (
              <Alert type="warning" showIcon style={{ marginBottom: 8 }}
                message={<ul style={{ paddingLeft: 18, margin: 0 }}>{d.uyarilar.filter((u) => !BANKA_HESAP_UYARI_RE.test(u)).map((u) => <li key={u}>{u}</li>)}</ul>} />
            )}
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Hedef Ay">{d.hedef_ay}/{d.hedef_yil}</Descriptions.Item>
              <Descriptions.Item label="Artış Ayı">
                {d.artis_ayi || '-'}{d.artis_ayi_turetilmis && <Tag color="orange" style={{ marginLeft: 4 }}>türetilmiş</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Baz (yıllık)">{fmtAmount(d.gecen_yil_kira)}</Descriptions.Item>
              <Descriptions.Item label="Baz Kaynağı">{bankaBazLabel(d.baz_kaynagi)}</Descriptions.Item>
              <Descriptions.Item label="Önceki Fatura" span={2}>
                {d.onceki_fatura
                  ? `${d.onceki_fatura.evrakNo || '(evraksız)'} · ${fmtDate(d.onceki_fatura.donemBaslangic)} - ${fmtDate(d.onceki_fatura.donemBitis)} · ${fmtAmount(d.onceki_tutar)} · ${d.onceki_fatura.durum}`
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Endeks Ayı">{d.endeks_ay}</Descriptions.Item>
              <Descriptions.Item label="Artış Oranı">
                %{d.artis_orani}
                {((d.artislar || []).length > 1 || (d.artislar || []).some((a) => !a.endeks_var))
                  && ` (${d.artislar.map((a) => `${a.endeks_ay}: ${a.endeks_var ? `%${a.oran}` : 'endeks yok'}`).join(' × ')})`}
              </Descriptions.Item>
              <Descriptions.Item label="Artış Endeksi" span={2}>
                {artisOraniLabel(d.endeks)}
                {d.endeks_sekli != null && d.endeks_sekli !== '' ? ` — ${artisSekliLabel(d.endeks_sekli)}` : ''}
              </Descriptions.Item>
              <Descriptions.Item label="Hesaplanan Kira">{fmtAmount(d.hesaplanan_kira)}</Descriptions.Item>
              <Descriptions.Item label="Gün">{d.gun}</Descriptions.Item>
              <Descriptions.Item label="Sonraki Artış">{fmtDate(d.sonraki_artis_tarihi)}</Descriptions.Item>
              <Descriptions.Item label="Son Defter Kaydı">{fmtDate(d.son_fatura_tarihi)}</Descriptions.Item>
            </Descriptions>
            <Form form={cForm} layout="vertical">
              <Space wrap>
                <Form.Item name="donem_baslangic" label="Dönem Başlangıç" rules={[{ required: true }]}><DatePicker format="DD.MM.YYYY" /></Form.Item>
                <Form.Item name="donem_bitis" label="Dönem Bitiş" rules={[{ required: true }]}><DatePicker format="DD.MM.YYYY" /></Form.Item>
              </Space>
              <Form.Item name="fatura_tutar" label="Fatura Tutarı (KDV hariç)" rules={[{ required: true }]}><InputNumber {...inputNumberMoneyProps} style={{ width: '100%' }} step={0.01} /></Form.Item>
              <Form.Item name="fatura_turu" label="Fatura Türü" rules={[{ required: true }]}><Select options={BANK_FATURA_TURU} /></Form.Item>
              <Form.Item name="banka_kodu" label="Banka Kodu"><Input /></Form.Item>
            </Form>
          </>
        )}
      </Modal>

      <Modal title="Fatura Kes" open={!!cutRow} onOk={doCut} confirmLoading={saving} onCancel={() => setCutRow(null)} okText="Kes" cancelText="Vazgeç" destroyOnClose>
        <Form form={kForm} layout="vertical">
          <Form.Item name="evrakNo" label="Evrak No" rules={[{ required: true, whitespace: true }]}><Input /></Form.Item>
          <Form.Item name="fatura_tarih" label="Fatura Tarihi" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" /></Form.Item>
          <Form.Item name="hesap" label="Hesap"><InputNumber style={{ width: '100%' }} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// =================================================================== YS Fatura
// 26.10 düzeltmesi (F2/F3/F5/F6):
// * Aylık Liste: etkin ödeme ayı (tanımsızsa başlangıç ayı, 'türetilmiş'), bitmiş/imzasız sözleşmeler yok,
//   seçili dönemin talep / ödeme talebi / ödeme durumu, eski Fedora son kira ödemesi, kabin/banka, mükerrer.
// * Hesapla: 'Artış ne zaman?' açıklaması; tek adım — Ödeme Vadesi + hissedar başına Finans cari kartı ile
//   kaydedince gft + defter + nf_odeme_talebi birlikte açılır (fatura no / fatura tarihi sorulmaz).
// * Talepler: 'Kes' yerine 'Ödeme Talebi Oluştur'; kabin/banka/kaynak/mükerrer kolonları; toplu yüklemeden
//   gelen ve dönemi geçmiş satırlar varsayılan görünümde gizli (ayrı filtre).
// 27.10 (inceleme): baz güvenilir değilse tutar önerilmez (referans + 'tutarı kontrol ettim' onayı);
//   Finans'ta silinmiş ödeme talebi yeniden açılabilir; 'Kart aç' benzer kartları gösterir.
const YS_TALEP_DURUMLARI = [
  { value: 'to-cut', label: 'Ödeme talebi açılmamış' }, { value: 'requested', label: 'Ödeme talebi açık' },
  { value: 'cut', label: 'Kesilmiş' }, { value: 'to-pay', label: 'Ödenecek' }, { value: 'paid', label: 'Ödenmiş' },
];
const YS_KAYNAKLAR = [
  { value: 'guncel', label: 'Güncel' }, { value: 'eski', label: 'Eski dönem (toplu yükleme)' }, { value: 'tumu', label: 'Tümü' },
];
const talepDurumTag = (v) => (String(v || '').startsWith('Ödendi (') ? <Tag color="green">{v}</Tag> : ({
  'Ödendi': <Tag color="green">Ödendi</Tag>, 'Ödeme Talebi Açıldı': <Tag color="blue">Ödeme talebi açık</Tag>,
  'Talep Var': <Tag color="gold">Talep var</Tag>,
}[v] || <Tag>Talep yok</Tag>));
// 27.10 (kullanıcı): dönemi ödenmiş kalem (yeni sistem ya da eski Fedora) pasif — soluk satır
const pasifSatir = (r) => ({ style: r.pasif ? { opacity: 0.5 } : undefined });
// 27.10 düzeltmesi: /ys-invoice/to-cut ve /ys-invoice/requests 'ORDER BY id DESC LIMIT 2000' ile
// kesiyor (contracts.py); lokasyon süzgeci istemcide olduğundan sınıra ulaşan listede o lokasyonun
// eski satırları sessizce düşebilir — süzgeç açıkken uyarı bu sınıra bakar.
const YS_LISTE_SINIR = 2000;

export function YsFaturaTab({ api, lokasyon, onLokasyonKaldir }) {
  const [view, setView] = useState('liste');
  const [tip, setTip] = useState('sabit');
  const [ay, setAy] = useState(dayjs().month() + 1);
  const [yil, setYil] = useState(dayjs().year());
  const [list, setList] = useState([]);
  const [talepler, setTalepler] = useState([]);
  const [talepStatus, setTalepStatus] = useState('to-cut');
  const [kaynak, setKaynak] = useState('guncel');
  const [loading, setLoading] = useState(false);
  const [calc, setCalc] = useState(null);
  const [hissedarlar, setHissedarlar] = useState([]);
  const [cForm] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [otTalepId, setOtTalepId] = useState(null);
  const [tutarOnay, setTutarOnay] = useState(false);
  const [modal, contextHolder] = Modal.useModal();
  const { options: cariOpts, reload: cariReload, finApi } = useCariSecenekleri(!!calc);

  // 27.10 düzeltmesi: ?lokasyon=<MaptriksID> (SozlesmeFaturaPage; lokasyon detayındaki YS paneli) —
  // Aylık Liste ve Talepler istemcide o lokasyona süzülür (to-cut ysk/bsk.lokasyon, requests
  // gft.lokasyon; ikisi de 'lokasyon' alanı). Üstteki etiket kapatılınca süzgeç (ve URL'deki değer) kalkar.
  const [lokFiltre, setLokFiltre] = useState(lokasyon ? String(lokasyon) : null);
  useEffect(() => { setLokFiltre(lokasyon ? String(lokasyon) : null); }, [lokasyon]);
  const gorunenList = React.useMemo(
    () => (lokFiltre ? list.filter((r) => String(r.lokasyon ?? '') === lokFiltre) : list), [list, lokFiltre]);
  const gorunenTalepler = React.useMemo(
    () => (lokFiltre ? talepler.filter((r) => String(r.lokasyon ?? '') === lokFiltre) : talepler), [talepler, lokFiltre]);
  const lokAd = lokFiltre ? [...gorunenList, ...gorunenTalepler].find((r) => r.lokasyon_ad)?.lokasyon_ad : null;
  const lokasyonKaldir = () => { setLokFiltre(null); if (onLokasyonKaldir) onLokasyonKaldir(); };
  // 27.10 düzeltmesi: süzgeçsiz liste sunucu sınırına (YS_LISTE_SINIR) ulaştıysa süzülmüş sonuç eksik olabilir.
  const hamUzunluk = (view === 'liste' ? list : talepler).length;
  const listeKesik = !!lokFiltre && hamUzunluk >= YS_LISTE_SINIR;
  const kesikNotu = `liste ${YS_LISTE_SINIR} satırda kesildi, eski kayıtlar görünmeyebilir`;

  const loadList = useCallback(async () => {
    setLoading(true);
    try { setList(await api.get('/ys-invoice/to-cut', { tip, ay, yil })); } catch { message.error('Yüklenemedi'); }
    finally { setLoading(false); }
  }, [api, tip, ay, yil]);
  const loadTalep = useCallback(async () => {
    setLoading(true);
    try { setTalepler(await api.get('/ys-invoice/requests', { status: talepStatus, kaynak })); } catch { message.error('Yüklenemedi'); }
    finally { setLoading(false); }
  }, [api, talepStatus, kaynak]);
  useEffect(() => { view === 'liste' ? loadList() : loadTalep(); }, [view, loadList, loadTalep]);

  const openCalc = async (row) => {
    if (tip !== 'banka' && !row.ysk_id) { message.warning('Bu satırda ysk yok'); return; }
    try {
      const d = await api.get(`/ys-invoice/calc/${row.ysk_id || 0}`, {
        tip, yil, ay, ...(tip === 'banka' ? { bsk_id: row.bsk_id } : {}),
      });
      setCalc({ row, data: d });
      setTutarOnay(false);
      setHissedarlar((d.hissedarlar || []).map((h, i) => ({ ...h, _k: i })));
      const bas = d.donem_baslangic ? dayjs(d.donem_baslangic) : null;
      cForm.setFieldsValue({
        donem_baslangic: bas,
        donem_bitis: d.donem_bitis ? dayjs(d.donem_bitis) : null,
        fatura_turu: d.fatura_turu || (tip === 'bos' ? 3 : (tip === 'banka' ? 2 : 1)),
        vade: bas && bas.isAfter(dayjs()) ? bas : dayjs(),
      });
    } catch (e) { message.error(hataMesaji(e, 'Hesaplanamadı')); }
  };
  const setHisse = (k, alan, val) => setHissedarlar((arr) => arr.map((h) => (h._k === k ? { ...h, [alan]: val } : h)));
  const hisseToplam = hissedarlar.reduce((s, h) => s + (Number(h.tutar) || 0), 0);
  const tutarNedeni = calc ? tutarKontrolNedeni(calc.data, hisseToplam) : null;
  const hisseCariAc = async (h) => {
    try {
      const id = await cariKartiAc(finApi, h.cari_adi, h.iban);
      await cariReload();
      if (id) setHisse(h._k, 'cari_id', id);
      message.success('Cari kartı açıldı');
    } catch (e) {
      message.error(e?.response?.status === 403
        ? 'Cari kartı açma yetkiniz yok — Finans → Cari ekranından açılmalı' : hataMesaji(e, 'Cari kartı açılamadı'));
    }
  };

  // odemeTalebi=true: tek adım (vade zorunlu, her hissedarın cari kartı zorunlu); false: yalnız gft talebi
  const kaydet = async (odemeTalebi) => {
    let v;
    try { v = await cForm.validateFields(); } catch { return; }
    if (odemeTalebi && !v.vade) { message.warning('Ödeme vadesi zorunlu'); return; }
    if (odemeTalebi && hissedarlar.some((h) => !h.cari_id)) {
      message.warning('Her hissedar için Finans cari kartı seçin (yoksa "Kart aç")');
      return;
    }
    if (hissedarlar.some((h) => !(Number(h.tutar) > 0))) { message.warning('Her hissedarın tutarını girin (0\'dan büyük)'); return; }
    if (tutarNedeni && !tutarOnay) { message.warning('Tutarı kontrol edip "Tutarı kontrol ettim" kutusunu işaretleyin'); return; }
    const { data } = calc;
    const govde = {
      ysk_id: data.ysk_id, bsk_id: data.bsk_id || undefined, lokasyon: data.lokasyon, kabin: data.kabin,
      firma: data.firma, banka: data.banka,
      donem_baslangic: dayjs(v.donem_baslangic).format('YYYY-MM-DD'),
      donem_bitis: dayjs(v.donem_bitis).format('YYYY-MM-DD'),
      fatura_turu: v.fatura_turu,
      fatura_tutar: Math.round(hisseToplam * 100) / 100,
      lines: hissedarlar.map((h) => ({ cari_adi: h.cari_adi, tutar: h.tutar, cari_id: h.cari_id, iban: h.iban || null })),
      ...(odemeTalebi ? { vade: dayjs(v.vade).format('YYYY-MM-DD') } : {}),
      tutar_kontrol_onay: !!(tutarNedeni && tutarOnay),
    };
    setSaving(true);
    try {
      const res = await cakismaOnayliGonder(modal, (force) => api.post(
        odemeTalebi ? '/ys-invoice/request-payment' : '/ys-invoice/request', { ...govde, force }));
      if (!res) return;
      message.success(res.message || 'Kaydedildi');
      setCalc(null);
      loadList();
    } catch (e) { message.error(hataMesaji(e, 'Kaydedilemedi')); }
    finally { setSaving(false); }
  };

  const [payRow, setPayRow] = useState(null);
  const [payAdet, setPayAdet] = useState(null);
  const [paying, setPaying] = useState(false);
  const markPaid = async () => {
    setPaying(true);
    try {
      await api.post(`/ys-invoice/${payRow.id}/pay`, { atm_adedi: payAdet });
      message.success('Ödendi'); setPayRow(null); setPayAdet(null); loadTalep();
    } catch (e) { message.error(hataMesaji(e, 'Hata')); }
    finally { setPaying(false); }
  };

  const listCols = [
    { title: 'Lokasyon', dataIndex: 'lokasyon_ad', ellipsis: true, render: (v, r) => v || r.lokasyon },
    // 27.10 (kullanıcı): kalemde YS bilgisi
    {
      title: 'YS Sözleşmesi', dataIndex: 'mUnvan', width: 220, ellipsis: true,
      render: (v, r) => (
        <span>
          {`#${r.sozlesme_id} ${v || ''}`}
          {r.kira_turu_isaretsiz ? (
            <Tooltip title="Sözleşmede kira türü (sabit / boş kabin / girdikçe) işaretli değil; sabit kira sayıldı">
              <Tag color="orange" style={{ marginLeft: 4 }}>Sözleşmeyi kontrol edin</Tag>
            </Tooltip>
          ) : null}
        </span>
      ),
    },
    ...(tip !== 'banka' ? [{ title: 'Kira Bedeli', dataIndex: 'kiraBedeli', width: 115, align: 'right', render: (v) => fmtAmount(v) }] : []),
    { title: 'Artış', dataIndex: 'artisOrani', width: 170, ellipsis: true, render: (v, r) => [artisOraniLabel(v), Number(r.artisSekli) ? artisSekliLabel(r.artisSekli) : ''].filter(Boolean).join(' / ') },
    ...(tip === 'banka' ? [{ title: 'Banka', dataIndex: 'banka_ad', width: 130, render: (v) => v || '-' }] : []),
    { title: 'Kabin', dataIndex: 'kabin', width: 70 },
    {
      title: 'Ödeme Ayı', dataIndex: 'odeme_ay', width: 120,
      render: (v, r) => <span>{v}{r.ay_turetilmis ? <Tag color="orange" style={{ marginLeft: 4 }}>türetilmiş</Tag> : null}</span>,
    },
    { title: 'Söz. Bitiş', dataIndex: 'bitis', width: 105, render: fmtDate },
    { title: 'Durum', dataIndex: 'talep_durumu', width: 150, render: talepDurumTag },
    // 27.10 (kullanıcı kararı): Eski Fedora / Mükerrer kolonları şimdilik gizli; engel talep açarken sunucuda
    { title: 'Aylık', dataIndex: 'aylik', width: 70, render: (v) => (v ? <Tag color="purple">Aylık</Tag> : '') },
    { title: 'Hisse', dataIndex: 'toplam_hisse', width: 70 },
    { title: 'KDV', dataIndex: 'kdv', width: 60, render: (v) => (v ? 'Evet' : 'Hayır') },
    {
      title: 'İşlem', key: 'a', fixed: 'right', width: 200, render: (_, r) => (
        <Space size={4}>
          <Button size="small" type={r.pasif ? 'default' : 'primary'} icon={<CalculatorOutlined />} onClick={() => openCalc(r)}>Hesapla</Button>
          <DuzenleKisayolu ys={r.sozlesme_id} ysk={r.ysk_id} bs={r.banka_sozlesme_id} bsk={r.bsk_id} />
        </Space>
      ),
    },
  ];
  const talepCols = [
    { title: 'Cari', dataIndex: 'cari_adi', ellipsis: true },
    { title: 'Lokasyon', dataIndex: 'lokasyon_ad', ellipsis: true, render: (v, r) => v || r.lokasyon },
    { title: 'Banka', dataIndex: 'banka_ad', width: 120, render: (v) => v || '-' },
    { title: 'Kabin', dataIndex: 'kabin', width: 65 },
    { title: 'Tür', dataIndex: 'fatura_turu_ad', width: 120 },
    { title: 'Dönem', dataIndex: 'donemBaslangic', width: 200, render: (v, r) => `${fmtDate(v)} - ${fmtDate(r.donemBitis)}` },
    { title: 'Tutar', dataIndex: 'fatura_tutar', width: 125, render: (v) => fmtAmount(v) },
    {
      title: 'Durum', dataIndex: 'durum_txt', width: 160, render: (v, r) => ({
        'Ödendi': <Tag color="green">Ödendi</Tag>,
        'Ödeme Talebi': <Tag color="blue">{`Ödeme talebi #${r.odeme_talebi_id}`}</Tag>,
        'Ödeme Talebi Silinmiş': <Tag>{`#${r.odeme_talebi_id} Finans'ta silinmiş`}</Tag>,
        'Kesildi': <Tag color="cyan">Kesildi</Tag>,
      }[v] || <Tag color="gold">Talep</Tag>),
    },
    { title: 'Vade', dataIndex: 'odeme_vade', width: 100, render: fmtDate },
    { title: 'Kaynak', dataIndex: 'toplu_yukleme', width: 120, render: (v) => (v ? <Tag>Toplu yükleme</Tag> : 'Kullanıcı') },
    {
      title: 'İşlem', key: 'a', fixed: 'right', width: 280, render: (_, r) => (
        <Space size={4}>
          <DuzenleKisayolu ys={r.sozlesme_id} ysk={r.ysk_eff} />
          {!r.odendi && !r.odeme_talebi_acik && (
            <Button size="small" type="primary" icon={<FileAddOutlined />} onClick={() => setOtTalepId(r.id)}>Ödeme Talebi Oluştur</Button>
          )}
          {r.kesildi && !r.odendi && !r.odeme_talebi_acik && !r.odeme_talebi_id && (
            <Button size="small" icon={<DollarOutlined />} onClick={() => setPayRow(r)}>Öde</Button>
          )}
        </Space>
      ),
    },
  ];
  const yillar = [yil - 1, yil, yil + 1].map((y) => ({ value: y, label: String(y) }));
  const listeKolonlari = enhanceColumns(view === 'liste' ? listCols : talepCols);

  return (
    <div>
      {contextHolder}
      <Space style={{ marginBottom: 12 }} wrap>
        <Radio.Group value={view} onChange={(e) => setView(e.target.value)} optionType="button" buttonStyle="solid"
          options={[{ value: 'liste', label: 'Aylık Liste' }, { value: 'talepler', label: 'Talepler' }]} />
        {view === 'liste' && (
          <>
            <Select value={tip} onChange={setTip} style={{ width: 150 }}
              options={[{ value: 'sabit', label: 'Sabit Kira' }, { value: 'banka', label: 'Banka Girdikçe' }, { value: 'bos', label: 'Boş Kabin' }]} />
            <Select value={ay} onChange={setAy} options={AYLAR} style={{ width: 90 }} />
            <Select value={yil} onChange={setYil} options={yillar} style={{ width: 100 }} />
          </>
        )}
        {view === 'talepler' && (
          <>
            <Select value={talepStatus} onChange={setTalepStatus} style={{ width: 210 }} options={YS_TALEP_DURUMLARI} />
            <Select value={kaynak} onChange={setKaynak} style={{ width: 220 }} options={YS_KAYNAKLAR} />
          </>
        )}
        <Button icon={<ReloadOutlined />} onClick={() => (view === 'liste' ? loadList() : loadTalep())}>Yenile</Button>
        <span style={{ color: '#888' }}>
          {(view === 'liste' ? gorunenList : gorunenTalepler).length} kayıt
          {lokFiltre ? ` (süzgeçsiz ${hamUzunluk})` : ''}
        </span>
        {listeKesik && (
          <Tooltip title={`Sunucu en yeni ${YS_LISTE_SINIR} satırı döndürüyor; lokasyon süzgeci bu satırlar üzerinde çalışır. Talep açmadan önce durum / kaynak süzgecini daraltın ya da Finans'ta kontrol edin.`}>
            <Tag color="orange">{kesikNotu}</Tag>
          </Tooltip>
        )}
        {lokFiltre && (
          <Tooltip title="Liste bu lokasyona süzülü; tümünü görmek için kapatın">
            <Tag color="geekblue" icon={<EnvironmentOutlined />} closable onClose={lokasyonKaldir}>
              {`Lokasyon: ${lokFiltre}${lokAd ? ` · ${lokAd}` : ''}`}
            </Tag>
          </Tooltip>
        )}
      </Space>
      <Table key={`${view}-${tip}`} rowKey={view === 'liste' ? (tip === 'banka' ? 'bsk_id' : 'ysk_id') : 'id'}
        size="small" loading={loading} dataSource={view === 'liste' ? gorunenList : gorunenTalepler} columns={listeKolonlari}
        onRow={view === 'liste' ? pasifSatir : undefined}
        locale={lokFiltre ? {
          emptyText: (view === 'liste'
            ? `Bu ay / türde ${lokFiltre} lokasyonu için satır yok — ay, yıl ya da türü değiştirin; açılmış talepler için 'Talepler'`
            : `${lokFiltre} lokasyonu için bu durum / kaynak süzgecinde talep yok — süzgeci değiştirin (ör. Kaynak: Tümü)`)
            + (listeKesik ? ` (uyarı: ${kesikNotu})` : ''),
        } : undefined}
        scroll={{ x: toplamGenislik(listeKolonlari), y: 460 }} pagination={{ pageSize: 50 }} />

      <Modal title={`YS Ödeme — ${payRow?.cari_adi || ''}`} open={!!payRow} onOk={markPaid} confirmLoading={paying}
        onCancel={() => { setPayRow(null); setPayAdet(null); }} okText="Ödendi İşaretle" cancelText="Vazgeç" destroyOnClose>
        <p>Tutar: <b>{fmtAmount(payRow?.fatura_tutar)}</b></p>
        <p style={{ color: '#888' }}>Not: Finans'ta açık ödeme talebi olan kayıtta bu işaret reddedilir (çift ödeme koruması).</p>
        <div style={{ marginBottom: 4 }}>ATM Adedi (bu ödemeye konu)</div>
        <InputNumber min={0} style={{ width: 160 }} value={payAdet} onChange={setPayAdet} placeholder="örn. 2" />
      </Modal>

      <Modal title={`YS Kira Hesapla & Ödeme Talebi${calc ? ` — ${calc.data.lokasyon_ad || calc.data.lokasyon}` : ''}`}
        open={!!calc} onCancel={() => setCalc(null)} width={900} destroyOnClose
        footer={[
          <Button key="v" onClick={() => setCalc(null)}>Vazgeç</Button>,
          <Button key="t" loading={saving} onClick={() => kaydet(false)}>Yalnız Talep Kaydet</Button>,
          <Button key="o" type="primary" loading={saving} onClick={() => kaydet(true)}>Kaydet ve Ödeme Talebi Oluştur</Button>,
        ]}>
        {calc && (
          <>
            <HesapAciklamasi data={calc.data} />
            {/* 27.10 (kullanıcı kararı): mevcut talep / eski Fedora kutuları gizli; risk varsa Kaydet'te onay penceresi çıkar */}
            <TutarKontrolKutusu neden={tutarNedeni} data={calc.data} onay={tutarOnay} setOnay={setTutarOnay}
              onDagit={() => setHissedarlar((arr) => referansDagit(arr))} />
            <Form form={cForm} layout="vertical" style={{ marginTop: 12 }}>
              <Space wrap>
                <Form.Item name="donem_baslangic" label="Dönem Başlangıç" rules={[{ required: true }]}><DatePicker format="DD.MM.YYYY" /></Form.Item>
                <Form.Item name="donem_bitis" label="Dönem Bitiş" rules={[{ required: true }]}><DatePicker format="DD.MM.YYYY" /></Form.Item>
                <Form.Item name="fatura_turu" label="Tür" rules={[{ required: true }]}><Select style={{ width: 160 }} options={YS_FATURA_TURU} /></Form.Item>
                <Form.Item name="vade" label="Ödeme Vadesi" rules={[{ required: true, message: 'Vade zorunlu' }]}><DatePicker format="DD.MM.YYYY" /></Form.Item>
              </Space>
            </Form>
            <Card size="small" title={(
              <span>Hissedarlar (her biri ayrı talep + ödeme talebi) — toplam {fmtAmount(hisseToplam)}
                {Math.abs(hisseToplam - (calc.data.fatura_tutar || 0)) > 0.05 && <Tag color="orange" style={{ marginLeft: 6 }}>hesaplanandan farklı</Tag>}
              </span>
            )}>
              <Table rowKey="_k" size="small" pagination={false} dataSource={hissedarlar}
                columns={[
                  {
                    title: 'Cari Adı', dataIndex: 'cari_adi', width: 200,
                    render: (v) => (v ? v : <Tag color="red">belirtilmemiş — talep oluşturulamaz</Tag>),
                  },
                  {
                    title: 'Finans Cari Kartı', dataIndex: 'cari_id', width: 300, render: (v, r) => (
                      <Space size={4}>
                        <Select size="small" showSearch allowClear optionFilterProp="label" options={cariOpts} value={v || undefined}
                          placeholder="Cari ara..." style={{ width: 220 }} status={v ? undefined : 'warning'}
                          onChange={(id) => setHisse(r._k, 'cari_id', id)} />
                        {!v && r.cari_adi && (
                          <Popconfirm title={cariAcBaslik(r.cari_adi, benzerCariler(cariOpts, r.cari_adi))} okText="Aç" cancelText="Vazgeç" onConfirm={() => hisseCariAc(r)}>
                            <Button size="small" type="dashed" icon={<PlusOutlined />}>Kart aç</Button>
                          </Popconfirm>
                        )}
                      </Space>
                    ),
                  },
                  { title: 'IBAN', dataIndex: 'iban', width: 210, render: (v, r) => <Input size="small" value={v || ''} onChange={(e) => setHisse(r._k, 'iban', e.target.value)} placeholder="TR.." /> },
                  { title: 'Oran', dataIndex: 'oran', width: 70, render: (v) => `%${(v * 100).toFixed(1)}` },
                  {
                    title: 'Tutar', dataIndex: 'tutar', width: 150, render: (v, r) => (
                      <InputNumber {...inputNumberMoneyProps} size="small" value={v} step={0.01} min={0}
                        status={Number(v) > 0 ? undefined : 'error'} placeholder={calc.data.tutar_onerildi === false ? 'elle girin' : undefined}
                        onChange={(val) => setHisse(r._k, 'tutar', val)} />
                    ),
                  },
                ]} />
            </Card>
          </>
        )}
      </Modal>

      <OdemeTalebiModal open={!!otTalepId} talepId={otTalepId} onClose={() => setOtTalepId(null)}
        onDone={() => { setOtTalepId(null); loadTalep(); }} />
    </div>
  );
}

// =================================================================== Sözleşmeler
// sozlesme-fatura.md §5.1
const ARTIS_SEKLI = { 0: 'Yok', 1: 'Yıllık Ortalama', 2: 'Önceki Yıl Aynı Ay', 3: '12 Aylık Ortalama' };
const ARTIS_ORANI = {
  0: 'Artış Yok', 1: 'ÜFE', 2: 'TÜFE', 3: '(ÜFE+TÜFE)/2', 4: 'İTO/TEFE', 5: 'Sabit Oran',
  6: '(ÜFE+TÜFE)/2 + Sabit', 7: 'Encümen', 8: 'Özel', 9: 'EuroZone', 10: 'Yeniden Değerleme',
};
// 27.10: sözleşme durumu (backend _soz_durum: imzalı 1/2/6 + süresi bitmemiş = Yürürlükte)
const DURUM_RENK = { 'Yürürlükte': 'green', 'Süresi Doldu': 'orange', 'Feshedildi': 'red', 'İptal': 'default', 'Aday (İmzasız)': 'blue', 'Belediye Kiracısı': 'purple' };
const durumKolonu = {
  title: 'Durum', dataIndex: 'durum', width: 120,
  filters: Object.keys(DURUM_RENK).map((d) => ({ text: d, value: d })),
  onFilter: (v, r) => r.durum === v,
  render: (v) => (v ? <Tag color={DURUM_RENK[v] || 'default'}>{v}</Tag> : ''),
};
const artisSekliLabel = (v) => ARTIS_SEKLI[Number(v)] ?? (v ?? '');
const artisOraniLabel = (v) => ARTIS_ORANI[Number(v)] ?? (v ?? '');

// sozlesme.Lokasyon bir JSON blob; içinden lokasyon adlarını çıkar.
const lokasyonAdlari = (json) => {
  if (!json) return '';
  try {
    const obj = JSON.parse(json);
    const adlar = Object.values(obj).map((x) => x && (x.ad || x.lokasyonNo)).filter(Boolean);
    return adlar.join(', ') || '';
  } catch {
    return typeof json === 'string' && json.length < 60 ? json : '';
  }
};

export function SozlesmelerTab({ api }) {
  const navigate = useNavigate();
  const [kind, setKind] = useState('sozlesme');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filesRow, setFilesRow] = useState(null);

  const lokasyonNoOf = (r) => {
    // 27.10: YS lokasyonları ys_sozlesme_kayit'ten (lokasyonlar); maptriksID çoğunda boş
    if (kind === 'ys_sozlesme') return r.maptriksID || String(r.lokasyonlar || '').split(',')[0].trim() || null;
    const raw = r.lokasyon_metin;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw).trim();
    if (/^\d+$/.test(s)) return s;                    // düz sayısal lokasyon/maptriks id
    try {
      const o = JSON.parse(s);
      if (typeof o === 'number') return String(o);
      const first = Object.values(o)[0];
      return (first && (first.lokasyonNo || first.maptriksID)) ? String(first.lokasyonNo || first.maptriksID) : null;
    } catch { return null; }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.list(kind, { genel_arama: search || undefined, limit: 500 })); } catch { message.error('Yüklenemedi'); }
    finally { setLoading(false); }
  }, [api, kind, search]);
  useEffect(() => { load(); }, [load]);

  const bankCols = [
    { title: 'Söz. No', dataIndex: 'sozlesmeNo', width: 120 },
    durumKolonu,
    { title: 'Banka', dataIndex: 'banka_ad', ellipsis: true },
    { title: 'Lokasyon', dataIndex: 'lokasyon_metin', ellipsis: true, render: (v) => lokasyonAdlari(v) },
    { title: 'Artış Oranı', dataIndex: 'artisOrani', width: 110, render: (v) => artisOraniLabel(v) },
    { title: 'Artış Şekli', dataIndex: 'artisSekli', width: 150, render: (v) => artisSekliLabel(v) },
    { title: 'Başlangıç', dataIndex: 'sozlesmeBaslangic', width: 110, render: fmtDate },
    { title: 'Bitiş', dataIndex: 'sozlesmeBitis', width: 110, render: fmtDate },
  ];
  const ysCols = [
    { title: 'Söz. No', dataIndex: 'sozlesmeNo', width: 120 },
    durumKolonu,
    { title: 'Unvan', dataIndex: 'mUnvan', ellipsis: true },
    {
      title: 'Lokasyon', dataIndex: 'lokasyonlar', width: 220, ellipsis: true,
      render: (v, r) => (v ? <Tooltip title={r.lokasyon_adlari}>{r.lokasyon_adlari ? `${v} - ${r.lokasyon_adlari}` : v}</Tooltip> : ''),
    },
    { title: 'Artış Oranı', dataIndex: 'artisOrani', width: 110, render: (v) => artisOraniLabel(v) },
    { title: 'Artış Şekli', dataIndex: 'artisSekli', width: 150, render: (v) => artisSekliLabel(v) },
    { title: 'Sabit', dataIndex: 'sabit_kira', width: 60, align: 'center', render: (v) => (v ? '✓' : '') },
    { title: 'Girdikçe', dataIndex: 'banka_girdikce_kira', width: 80, align: 'center', render: (v) => (v ? '✓' : '') },
    { title: 'Boş', dataIndex: 'bos_kabin_kira', width: 55, align: 'center', render: (v) => (v ? '✓' : '') },
    { title: 'KDV', dataIndex: 'kdv', width: 55, align: 'center', render: (v) => (v ? '✓' : '') },
    { title: 'Bitiş', dataIndex: 'bitis', width: 110, render: fmtDate },
  ];
  const actionCol = {
    title: 'İşlem', key: 'islem', fixed: 'right', width: 130,
    render: (_, r) => (
      <Space size={2}>
        <Tooltip title="Sözleşmeyi Düzenle">
          <Button size="small" icon={<EditOutlined />}
            onClick={() => {
              if (kind === 'sozlesme') {
                navigate(`/banka_sozlesme_detay/${r.id}`, {
                  state: { formId: 332, title: 'Sözleşme Metni', tableName: 'sozlesmeler', uniqueField: 'sozlesme', uniqueValue: r.id },
                });
              } else {
                navigate(`/yer_sozlesme_detay/${r.id}`, {
                  state: { formId: 333, title: 'Sözleşme Metni', tableName: 'sozlesmeler', uniqueField: 'sozlesmeNo', uniqueValue: r.id },
                });
              }
            }} />
        </Tooltip>
        <Tooltip title="Dosyalar">
          <Button size="small" icon={<FolderOpenOutlined />} onClick={() => setFilesRow(r)} />
        </Tooltip>
        <Tooltip title="Lokasyon Sözleşmeleri">
          <Button size="small" icon={<EnvironmentOutlined />}
            onClick={() => {
              const m = lokasyonNoOf(r);
              if (!m) { message.info('Bu sözleşmede lokasyon bilgisi yok'); return; }
              navigate(`/lokasyon_sozlesme/${m}${kind === 'ys_sozlesme' ? `?sozlesme=${r.id}` : ''}`, {
                state: { formId: 226, title: 'Lokasyon Sözleşmeleri', tableName: 'banka_sozlesme_kayit', uniqueField: 'maptriksID', uniqueValue: m },
              });
            }} />
        </Tooltip>
      </Space>
    ),
  };
  const cols = [...enhanceColumns(kind === 'sozlesme' ? bankCols : ysCols), actionCol];

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Radio.Group value={kind} onChange={(e) => setKind(e.target.value)} optionType="button" buttonStyle="solid"
          options={[{ value: 'sozlesme', label: 'Banka Sözleşmeleri' }, { value: 'ys_sozlesme', label: 'Yer Sahibi Sözleşmeleri' }]} />
        <Input.Search allowClear placeholder={kind === 'ys_sozlesme' ? 'Söz. no, unvan, lokasyon no/adı...' : 'Söz. no, lokasyon...'} style={{ width: 260 }} onSearch={setSearch} />
        <Button icon={<ReloadOutlined />} onClick={load}>Yenile</Button>
        <span style={{ color: '#888' }}>{data.length} kayıt</span>
      </Space>
      <Table rowKey="id" size="small" loading={loading} dataSource={data}
        columns={cols} scroll={{ x: toplamGenislik(cols), y: 480 }} pagination={{ pageSize: 50 }} />

      <Modal title={`Sözleşme Dosyaları — ${filesRow?.sozlesmeNo || ''}`} open={!!filesRow}
        footer={null} onCancel={() => setFilesRow(null)} width={760} destroyOnClose>
        {(() => {
          const files = sozlesmeFilePaths(filesRow);
          if (!files.length) return <Empty description="Bu sözleşmeye ait dosya bulunamadı" />;
          return (
            <Image.PreviewGroup>
              <Space wrap>
                {files.map((p, i) => {
                  const url = sozlesmeFileUrl(p);
                  const img = /\.(png|jpe?g|gif|webp|bmp)$/i.test(p);
                  return img
                    ? <Image key={i} src={url} width={150} height={150} style={{ objectFit: 'cover', borderRadius: 6 }} />
                    : <Button key={i} icon={<FileOutlined />} href={url} target="_blank" rel="noreferrer">{String(p).split('/').pop()}</Button>;
                })}
              </Space>
            </Image.PreviewGroup>
          );
        })()}
      </Modal>
    </div>
  );
}

// =================================================================== Defter
export function DefterTab({ api }) {
  const [kind, setKind] = useState('revenue');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lok, setLok] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.get(`/ledger/${kind}`, { lokasyon: lok || undefined, limit: 1000 })); } catch { message.error('Yüklenemedi'); }
    finally { setLoading(false); }
  }, [api, kind, lok]);
  useEffect(() => { load(); }, [load]);

  const cols = kind === 'revenue'
    ? [
        { title: 'Tarih', dataIndex: 'tarih', render: fmtDate },
        { title: 'Lokasyon', dataIndex: 'lokasyon_ad', ellipsis: true, render: (v, r) => v || r.lokasyon },
        { title: 'Banka', dataIndex: 'banka_ad', ellipsis: true },
        { title: 'Evrak No', dataIndex: 'evrak_no', width: 110 },
        { title: 'Kabin', dataIndex: 'kabin_no', width: 70 },
        { title: 'Tutar', dataIndex: 'tutar', render: (v) => fmtAmount(v) },
      ]
    : [
        { title: 'Tarih', dataIndex: 'tarih', render: fmtDate },
        { title: 'Lokasyon', dataIndex: 'lokasyon_ad', ellipsis: true, render: (v, r) => v || r.lokasyon },
        { title: 'Cari', dataIndex: 'cari_adi', ellipsis: true },
        { title: 'Evrak No', dataIndex: 'evrak_no', width: 110 },
        { title: 'KDV', dataIndex: 'kdv', width: 60, render: (v) => (v ? 'Evet' : 'Hayır') },
        { title: 'Tutar', dataIndex: 'tutar', render: (v) => fmtAmount(v) },
      ];
  const toplam = data.reduce((s, r) => s + (Number(r.tutar) || 0), 0);

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Radio.Group value={kind} onChange={(e) => setKind(e.target.value)} optionType="button" buttonStyle="solid"
          options={[{ value: 'revenue', label: 'Gelir (Kabin Kira)' }, { value: 'expense', label: 'Gider (Lokasyon Kira)' }]} />
        <Input allowClear placeholder="Lokasyon (MaptriksID)" style={{ width: 180 }} value={lok} onChange={(e) => setLok(e.target.value)} onPressEnter={load} />
        <Button icon={<ReloadOutlined />} onClick={load}>Yenile</Button>
        <Tag color="blue">Toplam: {fmtAmount(toplam)}</Tag>
      </Space>
      <Table rowKey="id" size="small" loading={loading} dataSource={data} columns={enhanceColumns(cols)} scroll={{ x: toplamGenislik(enhanceColumns(cols)), y: 480 }} pagination={{ pageSize: 100 }} />
    </div>
  );
}
